/**
 * Report tab of the creator page (SPEC §19.2, the 6a analysis on the design system): 11 KPI cards with
 * definitions, Wise Score scatter, posting heatmap (time zone, how often / when it works), length, audio and
 * hashtag leaders. Every number follows the window chosen above; definitions live in packages/core/src/report.ts.
 */
import { plural } from '@vw/core/src/plural';
import { useMemo, useState } from 'react';
import {
  audioLeaders, baselineDefinition, basedOn, durationBuckets, hashtagLeaders, heatmap, KPI_DEFINITIONS, reportCoverage, scatter, type ReportKpis, type ReportReel,
} from '@vw/core/src/report';
import { Segmented, Select, cn } from '@vw/ui';
import { Bars, Heatmap, Scatter } from '../Charts';
import { compact, duration, mult, pct } from '../../format';

const IST = 'Asia/Kolkata';
const raw = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
export const BROWSER_ZONE = raw === 'Asia/Calcutta' ? IST : raw;
const ZONES = [...new Set([IST, BROWSER_ZONE, 'UTC', 'America/New_York', 'Europe/London', 'Asia/Dubai', 'Asia/Singapore'])];

export function ReportTab({ reels, kpis, baseline, baselineN, scoreWindow = 0, threshold, notEnough, onPick }: { reels: ReportReel[]; kpis: ReportKpis; baseline: number | null; baselineN?: number | null; scoreWindow?: number; threshold: number; notEnough?: string; onPick: (pk: string) => void }) {
  const [zone, setZone] = useState(BROWSER_ZONE);
  const [mode, setMode] = useState<'count' | 'works'>('count');
  const hm = useMemo(() => heatmap(reels, zone, mode), [reels, zone, mode]);
  const sc = scatter(reels, baseline, threshold);
  const dur = durationBuckets(reels);
  const tags = hashtagLeaders(reels);
  const audio = audioLeaders(reels, threshold);
  const k = kpis;
  // Cards built from details a scan doesn't return for every reel say how many they cover (decision 71).
  const cov = reportCoverage(reels);
  const lengthNote = basedOn(cov.withLength, cov.reels);
  const audioNote = basedOn(cov.withAudio, cov.reels);
  const tagsNote = basedOn(cov.withCaption, cov.reels);
  const likesBasis = k.scoreBasis === 'likes';
  const card = 'rounded-3xl border border-border bg-card p-5 shadow-xs sm:p-6';
  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
        <Kpi label="Reels analysed" value={String(k.reels)} def={KPI_DEFINITIONS.reels} />
        <Kpi label="Total views" value={compact(k.totalViews)} def={KPI_DEFINITIONS.totalViews} />
        <Kpi label={likesBasis ? 'Usual likes' : 'Usual views'} value={compact(k.baselineViews)} sub={k.baselineViews === null && notEnough ? notEnough : `median here ${compact(k.medianViews)} · avg ${compact(k.avgViews)}`} def={baselineDefinition(k.baselineViews === null ? null : baselineN, scoreWindow)} strong />
        <Kpi label="Wise Score hit rate" value={pct(k.outlierHitRate, 0)} sub={`reels at ${mult(threshold)} or more`} def={KPI_DEFINITIONS.outlierHitRate} strong />
        <Kpi label="Total likes" value={compact(k.totalLikes)} sub={k.likesHiddenOn ? `hidden on ${k.likesHiddenOn}` : undefined} def={KPI_DEFINITIONS.totalLikes} />
        <Kpi label="Total comments" value={compact(k.totalComments)} def={KPI_DEFINITIONS.totalComments} />
        <Kpi label="Engagement" value={pct(k.medianEngagement)} def={KPI_DEFINITIONS.medianEngagement} />
        <Kpi label="Typical length" value={duration(k.medianDurationS)} def={KPI_DEFINITIONS.medianDurationS} />
        <Kpi label="Views ÷ followers" value={k.viewToFollower !== null && k.viewToFollower >= 1 ? mult(k.viewToFollower) : pct(k.viewToFollower, 0)} def={KPI_DEFINITIONS.viewToFollower} />
        <Kpi label="Posts per week" value={k.postsPerWeek === null ? '–' : String(k.postsPerWeek)} def={KPI_DEFINITIONS.postsPerWeek} />
        <Kpi label="Top audio" value={k.topAudio ? (k.topAudio.title ?? 'Original audio') : '–'} sub={k.topAudio ? `${plural(k.topAudio.count, 'reel')} at ${mult(threshold)}+` : undefined} def={KPI_DEFINITIONS.topAudio} />
      </section>

      <section className={card}>
        <h2 className="mb-3 font-extrabold">Wise Score over time</h2>
        <Scatter points={sc.points} baseline={sc.baseline} threshold={sc.threshold} onPick={onPick} />
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className={card}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-extrabold">When they post</h2>
            <div className="flex flex-wrap items-center gap-2">
              <Select aria-label="Time zone" value={zone} onChange={(e) => setZone(e.target.value)}>
                {ZONES.map((z) => <option key={z} value={z}>{z === IST ? 'India (IST)' : z === BROWSER_ZONE ? `${z} (yours)` : z}</option>)}
              </Select>
              <Segmented size="sm" value={mode} onChange={setMode} options={[{ value: 'count', label: 'How often' }, { value: 'works', label: 'When it works' }]} />
            </div>
          </div>
          <Heatmap cells={hm} mode={mode} />
        </section>
        <section className={card}>
          <h2 className="mb-3 font-extrabold">Length vs performance</h2>
          <Bars rows={dur.map((d) => ({ label: d.label, value: d.medianOutlier, note: `${d.reels} reel${d.reels === 1 ? '' : 's'}` }))} />
          <p className="mt-3 text-xs text-muted-foreground">Median Wise Score per length{lengthNote ? `, ${lengthNote}` : ''}.</p>
        </section>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className={card}>
          <h2 className="mb-3 font-extrabold">Audio</h2>
          {audio.original + audio.licensed > 0 ? (
            <>
              <p className="mb-2 text-xs text-muted-foreground">{audio.original} original · {audio.licensed} licensed{audioNote ? ` · ${audioNote}` : ''}</p>
              <ol className="list-decimal space-y-1.5 pl-5 text-sm">{audio.top.map((a) => <li key={a.id}><span className="flex justify-between gap-2"><span className="truncate">{a.title ?? 'Original audio'}{a.artist ? <span className="text-muted-foreground"> · {a.artist}</span> : null}</span><span className="shrink-0 text-xs text-muted-foreground">{a.outliers} at {mult(threshold)}+ / {a.reels}</span></span></li>)}</ol>
            </>
          ) : <p className="text-sm text-muted-foreground">Audio details appear once reels are scanned with audio info.</p>}
        </section>
        <section className={card}>
          <h2 className="mb-3 font-extrabold" title={tagsNote ? `From captions: ${tagsNote}` : undefined}>Hashtags that work</h2>
          {tags.length ? <ol className="list-decimal space-y-1.5 pl-5 text-sm">{tags.map((t) => <li key={t.tag}><span className="flex justify-between gap-2"><span>#{t.tag}</span><span className="text-xs text-muted-foreground">{mult(t.medianOutlier)} · {plural(t.reels, 'reel')}</span></span></li>)}</ol>
            : <p className="text-sm text-muted-foreground">Not enough hashtags used more than once{tagsNote ? ` (${tagsNote} have a caption)` : ''}.</p>}
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, def, strong }: { label: string; value: string; sub?: string; def: string; strong?: boolean }) {
  return (
    <div className={cn('cursor-help rounded-2xl border bg-card p-4 shadow-xs', strong ? 'border-primary/50' : 'border-border')} title={def}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-2xl font-extrabold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
